import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { ProgressBar } from "@/components/ProgressBar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { StudentCourseCalendar } from "@/components/StudentCourseCalendar";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getCourse,
  getCourseChapters,
  type ChapterDetails,
  getCourseLiveSessions,
  getCourseProgress,
  getMyEnrollments,
  type Course,
  formatCourseLevelsOr,
  enrollCourseWithBkash,
} from "@/api/lms/courses";
import { getCourseExams, ExamTypeBackend } from "@/api/lms/exams";
import { BookOpen, CalendarDays, FileText, Lock, PlayCircle, Radio, Video, Music } from "lucide-react";
import { CouponInput } from "@/components/CouponInput";
import type { CouponDiscountResult } from "@/api/lms/coupons";
import { usePaymentRedirectResult } from "@/hooks/usePaymentRedirectResult";
import { PolicyAcceptance } from "@/components/PolicyAcceptance";

function pct(progressPercentage: string | undefined | null): number {
  const n = Number(progressPercentage ?? 0);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function lessonHref(courseId: string, chapterId: string, lessonId: string) {
  return `/lesson?courseId=${encodeURIComponent(courseId)}&chapterId=${encodeURIComponent(chapterId)}&lessonId=${encodeURIComponent(lessonId)}`;
}

function findDefaultLesson(chapters: ChapterDetails[], currentChapterId: string | null) {
  const unlocked = chapters.filter((c) => !c.isLocked);
  if (currentChapterId) {
    const ch = unlocked.find((c) => c.id === currentChapterId);
    if (ch?.lessons?.length) return { chapterId: ch.id, lessonId: ch.lessons[0].id };
  }
  for (const ch of unlocked) {
    const first = ch.lessons?.[0];
    if (first) return { chapterId: ch.id, lessonId: first.id };
  }
  return null;
}

function lessonTypeIcon(type: "VIDEO" | "PDF" | "AUDIO" | "LIVE") {
  if (type === "VIDEO") return <Video size={14} className="text-primary shrink-0" />;
  if (type === "PDF") return <FileText size={14} className="text-warning shrink-0" />;
  if (type === "AUDIO") return <Music size={14} className="text-success shrink-0" />;
  return <Radio size={14} className="text-success shrink-0" />;
}

export default function CourseDetailsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const courseIdFromQuery = searchParams.get("courseId");

  const myEnrollmentsQuery = useQuery({
    queryKey: ["myEnrollmentsForCourseDetails"],
    queryFn: getMyEnrollments,
    enabled: Boolean(user),
  });

  // Report the outcome of a bKash checkout when the gateway redirects back here,
  // and refetch enrolments so the unlocked course renders without a manual reload.
  usePaymentRedirectResult(() => {
    queryClient.invalidateQueries({ queryKey: ["myEnrollmentsForCourseDetails"] });
  });

  const isStudent = !user || user?.backendRole === "STUDENT";
  const myCoursesPath = user?.backendRole === "TEACHER" ? "/teacher/courses" : "/courses";
  const enrollmentsReady = !user || myEnrollmentsQuery.isSuccess;

  const resolvedCourseId = useMemo(() => {
    if (courseIdFromQuery) return courseIdFromQuery;
    return myEnrollmentsQuery.data?.[0]?.course?.id ?? null;
  }, [courseIdFromQuery, myEnrollmentsQuery.data]);

  const isEnrolled = useMemo(() => {
    if (!user) return false;
    if (user.backendRole === "TEACHER" || user.backendRole === "SYSTEM_ADMIN") return true;
    if (!resolvedCourseId || !myEnrollmentsQuery.data) return false;
    return myEnrollmentsQuery.data.some((e) => e.course?.id === resolvedCourseId);
  }, [user, resolvedCourseId, myEnrollmentsQuery.data]);

  const courseQuery = useQuery({
    queryKey: ["courseDetails", resolvedCourseId],
    queryFn: () => getCourse(resolvedCourseId as string),
    enabled: Boolean(resolvedCourseId),
  });

  const chaptersQuery = useQuery({
    queryKey: ["courseChapters", resolvedCourseId],
    queryFn: () => getCourseChapters(resolvedCourseId as string),
    enabled: Boolean(resolvedCourseId),
  });

  const liveSessionsQuery = useQuery({
    queryKey: ["courseLiveSessions", resolvedCourseId],
    queryFn: () => getCourseLiveSessions(resolvedCourseId as string),
    enabled: Boolean(resolvedCourseId) && isEnrolled,
  });

  const progressQuery = useQuery({
    queryKey: ["courseProgress", resolvedCourseId],
    queryFn: () => getCourseProgress(resolvedCourseId as string),
    enabled: Boolean(resolvedCourseId) && isStudent && isEnrolled,
  });

  const courseExamsQuery = useQuery({
    queryKey: ["courseExams", resolvedCourseId],
    queryFn: () => getCourseExams(resolvedCourseId as string, { page: 1, limit: 100 }),
    enabled: Boolean(resolvedCourseId),
  });

  const [appliedCoupon, setAppliedCoupon] = useState<CouponDiscountResult | null>(null);
  // Student must accept the policies before enrolling. Gates both enrol buttons.
  const [policyAccepted, setPolicyAccepted] = useState(false);

  const enrollMutation = useMutation({
    mutationFn: () =>
      enrollCourseWithBkash(resolvedCourseId as string, appliedCoupon?.couponCode ?? undefined),
    onSuccess: (data) => {
      if (data.bkashURL) {
        toast.success("Redirecting to bKash payment...");
        window.location.href = data.bkashURL;
        return;
      }
      toast.success("Enrolled successfully! Welcome to the course.");
      queryClient.invalidateQueries({ queryKey: ["myEnrollmentsForCourseDetails"] });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to enroll");
    },
  });

  const course: Course | undefined = courseQuery.data as any;
  const rawChapters: ChapterDetails[] = chaptersQuery.data ?? [];
  const progress = progressQuery.data;
  const progressPct = pct(progress?.progressPercentage);
  const currentChapterId = progress?.currentChapterId ?? null;

  const chapters = useMemo(() => {
    if (!rawChapters.length) return [];
    if (user && (user.backendRole === "TEACHER" || user.backendRole === "SYSTEM_ADMIN")) {
      return rawChapters.map((ch) => ({ ...ch, isLocked: false }));
    }
    if (!isEnrolled) {
      return rawChapters.map((ch, idx) => ({ ...ch, isLocked: idx > 0 }));
    }
    const activeIndex = currentChapterId
      ? rawChapters.findIndex((c) => c.id === currentChapterId)
      : 0;
    const resolvedActiveIndex = activeIndex === -1 ? 0 : activeIndex;
    return rawChapters.map((ch, idx) => ({
      ...ch,
      isLocked: idx > resolvedActiveIndex,
    }));
  }, [rawChapters, user, isEnrolled, currentChapterId]);

  const defaultLesson = useMemo(
    () => (chapters.length ? findDefaultLesson(chapters, currentChapterId) : null),
    [chapters, currentChapterId],
  );

  const handleLockedClick = (e: React.MouseEvent, targetUrl: string) => {
    e.preventDefault();
    if (isEnrolled) {
      toast.warning("Please complete and pass the previous chapter's exam to unlock this content.");
      return;
    }
    if (!user) {
      navigate(`/login?returnTo=${encodeURIComponent(targetUrl)}`);
    } else {
      const ctaButton = document.getElementById("enroll-cta-button") || document.getElementById("enroll-cta-button-mobile");
      if (ctaButton) {
        ctaButton.scrollIntoView({ behavior: "smooth", block: "center" });
        ctaButton.classList.add("ring-4", "ring-primary", "ring-offset-2", "scale-105", "transition-all", "duration-300");
        setTimeout(() => {
          ctaButton.classList.remove("ring-4", "ring-primary", "ring-offset-2", "scale-105", "transition-all", "duration-300");
        }, 2000);
      }
    }
  };

  if (user && isStudent && !courseIdFromQuery && myEnrollmentsQuery.isPending) {
    return (
      <DashboardLayout showSidebar={isEnrolled}>
        <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-4">
          <Skeleton className="h-44 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (enrollmentsReady && !resolvedCourseId) {
    return (
      <DashboardLayout showSidebar={isEnrolled}>
        <div className="dash-page flex items-center justify-center">
          <div className="dash-empty-state">
            <p className="text-sm font-medium mb-4">No course selected. Go to “My Courses”.</p>
            <Button onClick={() => navigate(myCoursesPath)}>
              My Courses
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const loadingShell = courseQuery.isPending;

  return (
    <DashboardLayout showSidebar={isEnrolled}>
      <div className={cn("p-4 sm:p-6 md:p-10 max-w-5xl mx-auto w-full overflow-x-hidden", !isEnrolled && "pb-24")}>
        {loadingShell ? (
          <div className="space-y-4">
            <Skeleton className="h-44 w-full rounded-xl" />
            <Skeleton className="h-10 w-full rounded-xl" />
            <Skeleton className="h-10 w-full rounded-xl" />
          </div>
        ) : (
          <>
            <KCard className="p-6 md:p-8 mb-8">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {course?.thumbnail ? (
                    <img src={course.thumbnail} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <BookOpen className="text-primary" size={28} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-primary uppercase tracking-widest">
                      {course ? formatCourseLevelsOr(course, "Level") : "Level"}
                    </span>
                    <span className="text-xs text-muted-foreground">• {course?.type === "LIVE" ? "Live" : "Recorded"}</span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{course?.title ?? "Course"}</h1>
                  {course?.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{course.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <BookOpen size={14} /> {chapters.length} Chapters
                    </span>
                    <span className="flex items-center gap-1">
                      <PlayCircle size={14} />{" "}
                      {chapters.reduce((a, c) => a + (c.lessons?.length ?? 0), 0)} Lessons
                    </span>
                    <span className="flex items-center gap-1">
                      <CalendarDays size={14} /> Live schedule below
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0 w-full md:w-auto min-w-[160px]">
                  {isEnrolled ? (
                    defaultLesson ? (
                      <Button asChild size="lg" className="w-full">
                        <Link
                          to={lessonHref(resolvedCourseId, defaultLesson.chapterId, defaultLesson.lessonId)}
                        >
                          Continue learning
                        </Link>
                      </Button>
                    ) : (
                      <Button size="lg" disabled className="w-full">
                        No lessons yet
                      </Button>
                    )
                  ) : (
                    // Not enrolled: price → coupon → policy acceptance → action button.
                    // The gating checkbox sits ABOVE the button so its state is
                    // visible before the user reaches for the (disabled) CTA.
                    <>
                      {/* Coupon input — logged-in students on paid courses */}
                      {user && isStudent && course?.price && Number(course.price) > 0 && resolvedCourseId && (
                        <CouponInput
                          productType="COURSE"
                          productId={resolvedCourseId}
                          originalPrice={Number(course.price)}
                          currency="BDT"
                          onCouponApplied={(result) => setAppliedCoupon(result)}
                          onCouponCleared={() => setAppliedCoupon(null)}
                        />
                      )}
                      {course?.price && (
                        <p className="text-xs text-muted-foreground text-center">
                          {appliedCoupon
                            ? <span>Final Price: <span className="font-bold text-emerald-600">৳{appliedCoupon.finalPrice.toLocaleString()}</span></span>
                            : <span>Course Price: <span className="font-bold text-foreground">৳{course.price}</span></span>
                          }
                        </p>
                      )}
                      {/* Mandatory policy acceptance — above the button */}
                      {user && isStudent && (
                        <PolicyAcceptance
                          id="policy-accept-course-top"
                          checked={policyAccepted}
                          onChange={setPolicyAccepted}
                          className="mt-1"
                        />
                      )}
                      {user ? (
                        <Button
                          id="enroll-cta-button"
                          onClick={() => enrollMutation.mutate()}
                          disabled={enrollMutation.isPending || !policyAccepted}
                          size="lg"
                          className="w-full font-bold shadow-md transition-all duration-300"
                        >
                          {enrollMutation.isPending ? "Enrolling..." : "Enroll in Course"}
                        </Button>
                      ) : (
                        <Button
                          id="enroll-cta-button"
                          onClick={() => navigate(`/login?returnTo=${encodeURIComponent(`/course?courseId=${resolvedCourseId}`)}`)}
                          size="lg"
                          className="w-full font-bold shadow-md transition-all duration-300"
                        >
                          Unlock Full Course
                        </Button>
                      )}
                      {/* Explains why the button is disabled */}
                      {user && isStudent && !policyAccepted && (
                        <p className="text-[11px] text-muted-foreground text-center">
                          Please accept the policies above to continue.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
              {isStudent && isEnrolled && (
                <div className="mt-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Overall progress</span>
                    <span className="font-semibold text-foreground">{progressPct.toFixed(0)}%</span>
                  </div>
                  <ProgressBar progress={progressPct} size="md" />
                </div>
              )}
            </KCard>

            {isEnrolled && !chaptersQuery.isError && course?.type === "LIVE" && (
              <>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <CalendarDays size={22} className="text-primary" />
                  Schedule & live classes
                </h2>
                {chaptersQuery.isPending || liveSessionsQuery.isPending ? (
                  <Skeleton className="h-64 w-full rounded-xl mb-10" />
                ) : (
                  <div className="mb-10">
                    <StudentCourseCalendar
                      courseId={resolvedCourseId}
                      chapters={chapters}
                      liveSessions={liveSessionsQuery.data ?? []}
                    />
                  </div>
                )}
              </>
            )}

            <h2 className="text-xl font-bold text-foreground mb-4">Lessons</h2>
            {chaptersQuery.isPending ? (
              <Skeleton className="h-48 w-full rounded-xl" />
            ) : chapters.length === 0 ? (
              <div className="dash-empty-state py-8">
                <p className="text-sm font-medium">No chapters yet.</p>
              </div>
            ) : (
              <Accordion type="multiple" className="space-y-2">
                {chapters.map((ch, i) => {
                  const locked = Boolean(ch.isLocked);
                  const barrierExam =
                    (courseExamsQuery.data?.items ?? []).find(
                      (e) => e.type === ExamTypeBackend.recorded && e.chapterId === ch.id,
                    ) ?? null;
                  return (
                    <AccordionItem
                      key={ch.id}
                      value={ch.id}
                      className="rounded-xl border border-border bg-card px-2"
                    >
                      <AccordionTrigger className="px-3 py-3 hover:no-underline">
                        <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
                          {locked ? (
                            <Lock size={18} className="text-muted-foreground shrink-0" />
                          ) : (
                            <PlayCircle size={18} className="text-primary shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground truncate">
                              Chapter {i + 1}: {ch.title}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {(ch.lessons?.length ?? 0)} lessons
                              {locked ? " • Complete previous chapter to unlock" : ""}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3">
                        <div className="space-y-3">
                          <ul className="space-y-1">
                            {(ch.lessons ?? []).map((lesson) => {
                              const isLessonLocked = locked && !lesson.isPreview;
                              return (
                                <li key={lesson.id}>
                                  <Link
                                    to={isLessonLocked ? "#" : lessonHref(resolvedCourseId, ch.id, lesson.id)}
                                    onClick={(e) => {
                                      if (isLessonLocked) {
                                        handleLockedClick(
                                          e,
                                          lessonHref(resolvedCourseId, ch.id, lesson.id)
                                        );
                                      }
                                    }}
                                    className={cn(
                                      "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                                      "hover:bg-secondary text-foreground",
                                      isLessonLocked && "opacity-80"
                                    )}
                                  >
                                    {isLessonLocked ? <Lock size={14} className="text-muted-foreground shrink-0" /> : lessonTypeIcon(lesson.type)}
                                    <span className="font-medium flex-1 min-w-0 truncate">{lesson.title}</span>
                                    {lesson.isPreview && (
                                      <span className="text-[10px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full shrink-0">
                                        Preview
                                      </span>
                                    )}
                                    {isLessonLocked && (
                                      <span className="text-[10px] font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full shrink-0">
                                        Premium
                                      </span>
                                    )}
                                    <span className="text-[10px] uppercase font-bold text-muted-foreground shrink-0">
                                      {lesson.type}
                                    </span>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>

                          {/* Chapter exam */}
                          <div className="border border-border/60 rounded-xl p-3 bg-secondary/20">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                                  {locked && <Lock size={14} className="text-muted-foreground" />}
                                  Chapter {i + 1} exam
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {barrierExam
                                    ? locked
                                      ? "Enroll in course to unlock exam"
                                      : "Pass to unlock the next chapter."
                                    : "Not created yet by the teacher."}
                                </p>
                              </div>
                              {barrierExam ? (
                                <Button
                                  size="sm"
                                  variant={locked ? "outline" : isEnrolled ? "secondary" : "default"}
                                  className={cn("w-full sm:w-auto", locked && "opacity-80 cursor-not-allowed")}
                                  asChild
                                >
                                  <Link
                                    to={locked ? "#" : `/exam?courseId=${encodeURIComponent(resolvedCourseId)}&examId=${encodeURIComponent(barrierExam.id)}`}
                                    onClick={(e) => {
                                      if (locked) {
                                        handleLockedClick(
                                          e,
                                          `/exam?courseId=${encodeURIComponent(resolvedCourseId)}&examId=${encodeURIComponent(barrierExam.id)}`
                                        );
                                      }
                                    }}
                                  >
                                    {locked ? "Locked Exam" : "Give exam"}
                                  </Link>
                                </Button>
                              ) : (
                                <Button size="sm" variant="secondary" className="w-full sm:w-auto" disabled>
                                  Give exam
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}

            {chaptersQuery.isError && (
              <div className="dash-empty-state py-8 border-destructive/30 bg-destructive/5">
                <p className="text-destructive text-sm font-medium">Could not load course content.</p>
                <p className="text-muted-foreground text-sm mt-1">Try again or contact support if this persists.</p>
              </div>
            )}
          </>
        )}

        {/* Sticky Mobile CTA Bar */}
        {!isEnrolled && course && (
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border px-6 py-4 flex items-center justify-between shadow-2xl md:hidden">
            <div className="min-w-0 flex-1 mr-4">
              <p className="text-xs text-muted-foreground truncate">{course.title}</p>
              <p className="text-sm font-bold text-foreground">
                {course.price && Number(course.price) > 0 ? `৳${course.price}` : "Free"}
              </p>
            </div>
            {user ? (
              <Button
                id="enroll-cta-button-mobile"
                onClick={() => {
                  // Policy acceptance is mandatory. If not yet accepted, guide the
                  // user to the checkbox rather than enrolling.
                  if (!policyAccepted) {
                    toast.info("Please accept the Terms, Privacy and Refund policies to continue.");
                    document
                      .getElementById("policy-accept-course-top")
                      ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    return;
                  }
                  enrollMutation.mutate();
                }}
                disabled={enrollMutation.isPending}
                size="sm"
                className="shrink-0 font-bold transition-all duration-300"
              >
                {enrollMutation.isPending ? "Enrolling..." : "Enroll Now"}
              </Button>
            ) : (
              <Button
                id="enroll-cta-button-mobile"
                onClick={() => navigate(`/login?returnTo=${encodeURIComponent(`/course?courseId=${resolvedCourseId}`)}`)}
                size="sm"
                className="shrink-0 font-bold transition-all duration-300"
              >
                Unlock Course
              </Button>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
